/**
 * JSON annotation messages over RTCDataChannel "annotations".
 * Coordinates are normalized to 0–1 relative to the overlay canvas (display box).
 */

export type AnnotationTool =
    | 'pencil'
    | 'highlighter'
    | 'rectangle'
    | 'circle'
    | 'line'
    | 'eraser'
    /** Bounding-box diamond (shape messages only; same coordinate model as rectangle). */
    | 'diamond'
    /** Placed label (single-shot message, not freehand). */
    | 'text';

export type NormPoint = {x: number; y: number};

export type AnnotationMessage =
    | {
          type: 'start';
          userId: string;
          tool: AnnotationTool;
          color: string;
          width: number;
          strokeId: string;
          opacity?: number;
      }
    | {
          type: 'draw';
          userId: string;
          tool: AnnotationTool;
          color: string;
          width: number;
          points: NormPoint[];
          strokeId: string;
          opacity?: number;
      }
    | {
          type: 'end';
          userId: string;
          strokeId: string;
      }
    | {
          type: 'shape';
          userId: string;
          tool: 'rectangle' | 'circle' | 'line' | 'diamond';
          color: string;
          width: number;
          strokeId: string;
          from: NormPoint;
          to: NormPoint;
          opacity?: number;
      }
    | {
          type: 'clear';
          userId: string;
          /** If true, wipe everything (sender “clear all”). */
          all?: boolean;
      }
    | {
          type: 'undo';
          userId: string;
          strokeId?: string;
      }
    | {
          type: 'redo';
          userId: string;
          strokeId: string;
          messages: AnnotationMessage[];
      }
    | {
          type: 'sync';
          userId: string;
          messages: AnnotationMessage[];
      }
    | {
          /** Move all ink in normalized content space (broadcast by presenter). */
          type: 'layerPan';
          userId: string;
          x: number;
          y: number;
      }
    | {
          /** Move one stroke/shape/text by delta in normalized content space (pan tool + selection). */
          type: 'strokeMove';
          userId: string;
          strokeId: string;
          dx: number;
          dy: number;
      }
    | {
          type: 'text';
          userId: string;
          strokeId: string;
          /** Content-space anchor (baseline left), normalized to video render box. */
          x: number;
          y: number;
          text: string;
          color: string;
          /** CSS px at 1:1 with canvas CSS pixels (scaled in renderer). */
          fontSize: number;
      };

export function parseAnnotationMessage(raw: string): AnnotationMessage | null {
    try {
        const v = JSON.parse(raw) as AnnotationMessage;
        if (!v || typeof v !== 'object' || !('type' in v)) {
            return null;
        }
        return v;
    } catch {
        return null;
    }
}

export function serializeAnnotationMessage(msg: AnnotationMessage): string {
    return JSON.stringify(msg);
}

/** Deterministic color per remote user for multi-drawer UX. */
export function colorForUserId(userId: string): string {
    let h = 0;
    for (let i = 0; i < userId.length; i++) {
        h = (h * 31 + userId.charCodeAt(i)) >>> 0;
    }
    const hue = h % 360;
    return `hsl(${hue} 75% 52%)`;
}
